import { Link } from "react-router-dom"
import { useEffect, useRef } from "react"
import {
  Leaf,
  Award,
  Users,
  Heart,
  Shield,
  Truck,
  Clock,
  CheckCircle,
  Star,
  Target,
  Eye,
  Gem,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Quote,
  Sparkles,
  Globe,
  Lightbulb,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useLanguage } from "@/lib/language-context"

export function AboutContent() {
  const { t, currentLang } = useLanguage()
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    )

    document
      .querySelectorAll(".scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale")
      .forEach((el) => {
        observerRef.current?.observe(el)
      })

    return () => observerRef.current?.disconnect()
  }, [])

  const translations = {
    uz: {
      // Hero Section
      hero_badge: "15 yillik tajriba",
      hero_title: "Tabiat Kuchi Bilan",
      hero_title_accent: "Sog'lom Kelajak",
      hero_subtitle:
        "Biz 2009-yildan beri O'zbekiston bo'ylab tabiiy dori-darmonlar va sog'liqni saqlash mahsulotlarini ishlab chiqarish va yetkazib berish bilan shug'ullanamiz.",
      hero_cta: "Bog'lanish",
      hero_cta_secondary: "Mahsulotlar",

      // Stats
      stat_years: "Yillik tajriba",
      stat_customers: "Mamnun mijozlar",
      stat_products: "Mahsulot turlari",
      stat_regions: "Viloyat",

      // Mission Section
      mission_badge: "Bizning maqsadimiz",
      mission_title: "Har Bir Oilaga Sog'liq",
      mission_text:
        "Bizning asosiy maqsadimiz - har bir O'zbek oilasiga sifatli, tabiiy va arzon narxlardagi sog'liqni saqlash mahsulotlarini yetkazish. Biz tabiatning in'omlaridan foydalanib, zamonaviy texnologiyalar yordamida eng yaxshi mahsulotlarni yaratamiz.",

      // Vision Section
      vision_badge: "Kelajak uchun",
      vision_title: "Bizning Qarashimiz",
      vision_text:
        "2030-yilgacha Markaziy Osiyoning yetakchi tabiiy mahsulotlar kompaniyasiga aylanish va millionlab odamlarning sog'lig'ini yaxshilashga hissa qo'shish.",

      // Values Section
      values_badge: "Qadriyatlar",
      values_title: "Bizning Qadriyatlarimiz",
      values_subtitle: "Biz ishimizda quyidagi tamoyillarga amal qilamiz",
      value_1_title: "Sifat",
      value_1_desc: "Faqat eng yuqori sifatli tabiiy ingredientlar",
      value_2_title: "Ishonch",
      value_2_desc: "Mijozlarimiz bilan uzoq muddatli munosabatlar",
      value_3_title: "Innovatsiya",
      value_3_desc: "Zamonaviy texnologiyalar va ilmiy yondashuvlar",
      value_4_title: "Mas'uliyat",
      value_4_desc: "Ekologik toza ishlab chiqarish va qadoqlash",

      // History Section
      history_badge: "Bizning tarix",
      history_title: "Rivojlanish Yo'limiz",
      history_subtitle: "2009-yildan bugungi kungacha bo'lgan sayohatimiz",
      history_1_year: "2009",
      history_1_title: "Asos solinishi",
      history_1_desc: "Kichik oilaviy biznes sifatida faoliyatni boshladik",
      history_2_year: "2012",
      history_2_title: "Birinchi zavod",
      history_2_desc: "O'z ishlab chiqarish zavodimizni ochdik",
      history_3_year: "2016",
      history_3_title: "Sertifikatsiya",
      history_3_desc: "Xalqaro sifat sertifikatlarini oldik",
      history_4_year: "2019",
      history_4_title: "Eksport",
      history_4_desc: "Qo'shni davlatlarga eksportni boshladik",
      history_5_year: "2022",
      history_5_title: "Kengayish",
      history_5_desc: "Barcha viloyatlarda tarqatish tarmog'ini yo'lga qo'ydik",
      history_6_year: "2024",
      history_6_title: "Bugun",
      history_6_desc: "500+ mahsulot va 100,000+ mamnun mijoz",

      // Team Section
      team_badge: "Bizning jamoa",
      team_title: "Professional Mutaxassislar",
      team_subtitle: "Tajribali va fidoyi jamoamiz sizga xizmat qilishdan mamnun",
      team_1_name: "Akmal Karimov",
      team_1_role: "Bosh direktor",
      team_2_name: "Nilufar Rahimova",
      team_2_role: "Ishlab chiqarish boshlig'i",
      team_3_name: "Sardor Aliyev",
      team_3_role: "Sifat nazorati",
      team_4_name: "Madina Umarova",
      team_4_role: "Mijozlar xizmati",

      // Certificates Section
      certificates_badge: "Sertifikatlar",
      certificates_title: "Sifat Kafolatlari",
      certificates_subtitle: "Barcha mahsulotlarimiz tegishli sertifikatlarga ega",
      cert_1: "Halol sertifikati",
      cert_2: "ISO 9001:2015",
      cert_3: "GMP standartlari",
      cert_4: "Ekologik sertifikat",

      // Process Section
      process_badge: "Ishlab chiqarish",
      process_title: "Qanday Ishlaymiz",
      process_subtitle: "Sifatli mahsulot yaratish jarayonimiz",
      process_1_title: "Xom ashyo tanlash",
      process_1_desc: "Faqat sertifikatlangan yetkazib beruvchilardan",
      process_2_title: "Laboratoriya tekshiruvi",
      process_2_desc: "Har bir partiya tekshiriladi",
      process_3_title: "Ishlab chiqarish",
      process_3_desc: "Zamonaviy uskunalarda",
      process_4_title: "Sifat nazorati",
      process_4_desc: "Ko'p bosqichli tekshiruvlar",
      process_5_title: "Qadoqlash",
      process_5_desc: "Ekologik toza materiallar",
      process_6_title: "Yetkazib berish",
      process_6_desc: "Tez va ishonchli",

      // Partners Section
      partners_badge: "Hamkorlar",
      partners_title: "Ishonchli Hamkorlarimiz",
      partners_subtitle: "Yetakchi tashkilotlar bilan hamkorlik qilamiz",

      // Testimonials Section
      testimonials_badge: "Mijozlar fikri",
      testimonials_title: "Ishonch Ovozi",
      testimonials_subtitle: "Mijozlarimiz biz haqimizda nima deyishadi",
      testimonial_1_text:
        "TabiatShop mahsulotlari menga sog'ligimni yaxshilashda juda yordam berdi. 5 yildan beri faqat ulardan xarid qilaman.",
      testimonial_1_name: "Dilnoza Qodirova",
      testimonial_1_role: "Toshkent",
      testimonial_2_text: "Sifat va narx nisbati ajoyib. Yetkazib berish tez va xodimlar juda mehribon.",
      testimonial_2_name: "Jamshid Normatov",
      testimonial_2_role: "Samarqand",
      testimonial_3_text: "Dorixonamiz uchun asosiy yetkazib beruvchi. Mijozlarimiz mahsulotlardan mamnun.",
      testimonial_3_name: "Shaxnoza Karimova",
      testimonial_3_role: "Farg'ona",

      // CTA Section
      cta_title: "Biz Bilan Bog'laning",
      cta_subtitle: "Savollaringiz bormi? Biz har doim yordam berishga tayyormiz.",
      cta_phone: "Telefon",
      cta_email: "Email",
      cta_address: "Manzil",
      cta_button: "Xabar yuborish",

      // Footer CTA
      footer_cta_title: "Sog'lom Hayot Uchun Birinchi Qadamni Tashlang",
      footer_cta_text: "Bugun bizning mahsulotlarimizni sinab ko'ring va farqni his qiling.",
      footer_cta_button: "Xarid qilish",
    },
    en: {
      hero_badge: "15 years of experience",
      hero_title: "Healthy Future With",
      hero_title_accent: "Nature's Power",
      hero_subtitle:
        "Since 2009, we have been producing and delivering natural medicines and health products throughout Uzbekistan.",
      hero_cta: "Contact Us",
      hero_cta_secondary: "Products",

      stat_years: "Years Experience",
      stat_customers: "Happy Customers",
      stat_products: "Product Types",
      stat_regions: "Regions",

      mission_badge: "Our Mission",
      mission_title: "Health for Every Family",
      mission_text:
        "Our main goal is to deliver quality, natural and affordable health products to every Uzbek family. We use nature's gifts and modern technologies to create the best products.",

      vision_badge: "For the Future",
      vision_title: "Our Vision",
      vision_text:
        "To become Central Asia's leading natural products company by 2030 and contribute to improving the health of millions.",

      values_badge: "Values",
      values_title: "Our Core Values",
      values_subtitle: "We follow these principles in our work",
      value_1_title: "Quality",
      value_1_desc: "Only the highest quality natural ingredients",
      value_2_title: "Trust",
      value_2_desc: "Long-term relationships with our customers",
      value_3_title: "Innovation",
      value_3_desc: "Modern technologies and scientific approaches",
      value_4_title: "Responsibility",
      value_4_desc: "Eco-friendly production and packaging",

      history_badge: "Our History",
      history_title: "Our Journey",
      history_subtitle: "Our journey from 2009 to today",
      history_1_year: "2009",
      history_1_title: "Foundation",
      history_1_desc: "Started as a small family business",
      history_2_year: "2012",
      history_2_title: "First Factory",
      history_2_desc: "Opened our own production facility",
      history_3_year: "2016",
      history_3_title: "Certification",
      history_3_desc: "Received international quality certificates",
      history_4_year: "2019",
      history_4_title: "Export",
      history_4_desc: "Started exporting to neighboring countries",
      history_5_year: "2022",
      history_5_title: "Expansion",
      history_5_desc: "Established distribution network in all regions",
      history_6_year: "2024",
      history_6_title: "Today",
      history_6_desc: "500+ products and 100,000+ happy customers",

      team_badge: "Our Team",
      team_title: "Professional Experts",
      team_subtitle: "Our experienced and dedicated team is happy to serve you",
      team_1_name: "Akmal Karimov",
      team_1_role: "CEO",
      team_2_name: "Nilufar Rahimova",
      team_2_role: "Production Manager",
      team_3_name: "Sardor Aliyev",
      team_3_role: "Quality Control",
      team_4_name: "Madina Umarova",
      team_4_role: "Customer Service",

      certificates_badge: "Certificates",
      certificates_title: "Quality Guarantees",
      certificates_subtitle: "All our products have relevant certificates",
      cert_1: "Halal Certificate",
      cert_2: "ISO 9001:2015",
      cert_3: "GMP Standards",
      cert_4: "Eco Certificate",

      process_badge: "Production",
      process_title: "How We Work",
      process_subtitle: "Our quality product creation process",
      process_1_title: "Raw Material Selection",
      process_1_desc: "Only from certified suppliers",
      process_2_title: "Laboratory Testing",
      process_2_desc: "Every batch is tested",
      process_3_title: "Manufacturing",
      process_3_desc: "With modern equipment",
      process_4_title: "Quality Control",
      process_4_desc: "Multi-stage inspections",
      process_5_title: "Packaging",
      process_5_desc: "Eco-friendly materials",
      process_6_title: "Delivery",
      process_6_desc: "Fast and reliable",

      partners_badge: "Partners",
      partners_title: "Our Trusted Partners",
      partners_subtitle: "We work with leading organizations",

      testimonials_badge: "Customer Reviews",
      testimonials_title: "Voice of Trust",
      testimonials_subtitle: "What our customers say about us",
      testimonial_1_text:
        "TabiatShop products have helped me a lot in improving my health. I've been buying from them for 5 years.",
      testimonial_1_name: "Dilnoza Qodirova",
      testimonial_1_role: "Tashkent",
      testimonial_2_text: "The quality-price ratio is excellent. Delivery is fast and staff are very friendly.",
      testimonial_2_name: "Jamshid Normatov",
      testimonial_2_role: "Samarkand",
      testimonial_3_text: "Main supplier for our pharmacy. Our customers are satisfied with the products.",
      testimonial_3_name: "Shaxnoza Karimova",
      testimonial_3_role: "Fergana",

      cta_title: "Get in Touch",
      cta_subtitle: "Have questions? We're always ready to help.",
      cta_phone: "Phone",
      cta_email: "Email",
      cta_address: "Address",
      cta_button: "Send Message",

      footer_cta_title: "Take the First Step to a Healthy Life",
      footer_cta_text: "Try our products today and feel the difference.",
      footer_cta_button: "Shop Now",
    },
    ru: {
      hero_badge: "15 лет опыта",
      hero_title: "Здоровое Будущее с",
      hero_title_accent: "Силой Природы",
      hero_subtitle:
        "С 2009 года мы производим и доставляем натуральные лекарства и продукты для здоровья по всему Узбекистану.",
      hero_cta: "Связаться",
      hero_cta_secondary: "Продукты",

      stat_years: "Лет опыта",
      stat_customers: "Довольных клиентов",
      stat_products: "Видов продукции",
      stat_regions: "Регионов",

      mission_badge: "Наша миссия",
      mission_title: "Здоровье для Каждой Семьи",
      mission_text:
        "Наша главная цель - доставить качественные, натуральные и доступные продукты для здоровья каждой узбекской семье. Мы используем дары природы и современные технологии для создания лучших продуктов.",

      vision_badge: "Для будущего",
      vision_title: "Наше Видение",
      vision_text:
        "Стать ведущей компанией натуральных продуктов в Центральной Азии к 2030 году и внести вклад в улучшение здоровья миллионов людей.",

      values_badge: "Ценности",
      values_title: "Наши Ценности",
      values_subtitle: "Мы следуем этим принципам в работе",
      value_1_title: "Качество",
      value_1_desc: "Только натуральные ингредиенты высшего качества",
      value_2_title: "Доверие",
      value_2_desc: "Долгосрочные отношения с клиентами",
      value_3_title: "Инновации",
      value_3_desc: "Современные технологии и научные подходы",
      value_4_title: "Ответственность",
      value_4_desc: "Экологичное производство и упаковка",

      history_badge: "Наша история",
      history_title: "Наш Путь",
      history_subtitle: "Наш путь с 2009 года до сегодня",
      history_1_year: "2009",
      history_1_title: "Основание",
      history_1_desc: "Начали как небольшой семейный бизнес",
      history_2_year: "2012",
      history_2_title: "Первый завод",
      history_2_desc: "Открыли собственное производство",
      history_3_year: "2016",
      history_3_title: "Сертификация",
      history_3_desc: "Получили международные сертификаты качества",
      history_4_year: "2019",
      history_4_title: "Экспорт",
      history_4_desc: "Начали экспорт в соседние страны",
      history_5_year: "2022",
      history_5_title: "Расширение",
      history_5_desc: "Создали сеть распространения во всех регионах",
      history_6_year: "2024",
      history_6_title: "Сегодня",
      history_6_desc: "500+ продуктов и 100,000+ довольных клиентов",

      team_badge: "Наша команда",
      team_title: "Профессиональные Эксперты",
      team_subtitle: "Наша опытная команда рада вам служить",
      team_1_name: "Акмаль Каримов",
      team_1_role: "Генеральный директор",
      team_2_name: "Нилуфар Рахимова",
      team_2_role: "Руководитель производства",
      team_3_name: "Сардор Алиев",
      team_3_role: "Контроль качества",
      team_4_name: "Мадина Умарова",
      team_4_role: "Обслуживание клиентов",

      certificates_badge: "Сертификаты",
      certificates_title: "Гарантии Качества",
      certificates_subtitle: "Все наши продукты имеют соответствующие сертификаты",
      cert_1: "Халяль сертификат",
      cert_2: "ISO 9001:2015",
      cert_3: "Стандарты GMP",
      cert_4: "Эко сертификат",

      process_badge: "Производство",
      process_title: "Как Мы Работаем",
      process_subtitle: "Процесс создания качественного продукта",
      process_1_title: "Выбор сырья",
      process_1_desc: "Только от сертифицированных поставщиков",
      process_2_title: "Лабораторные тесты",
      process_2_desc: "Каждая партия проверяется",
      process_3_title: "Производство",
      process_3_desc: "На современном оборудовании",
      process_4_title: "Контроль качества",
      process_4_desc: "Многоэтапные проверки",
      process_5_title: "Упаковка",
      process_5_desc: "Экологичные материалы",
      process_6_title: "Доставка",
      process_6_desc: "Быстро и надежно",

      partners_badge: "Партнеры",
      partners_title: "Наши Надежные Партнеры",
      partners_subtitle: "Мы работаем с ведущими организациями",

      testimonials_badge: "Отзывы клиентов",
      testimonials_title: "Голос Доверия",
      testimonials_subtitle: "Что говорят о нас клиенты",
      testimonial_1_text: "Продукты TabiatShop очень помогли мне улучшить здоровье. Покупаю у них уже 5 лет.",
      testimonial_1_name: "Дильноза Кодирова",
      testimonial_1_role: "Ташкент",
      testimonial_2_text: "Отличное соотношение цены и качества. Доставка быстрая, персонал очень дружелюбный.",
      testimonial_2_name: "Джамшид Норматов",
      testimonial_2_role: "Самарканд",
      testimonial_3_text: "Основной поставщик для нашей аптеки. Клиенты довольны продукцией.",
      testimonial_3_name: "Шахноза Каримова",
      testimonial_3_role: "Фергана",

      cta_title: "Свяжитесь с Нами",
      cta_subtitle: "Есть вопросы? Мы всегда готовы помочь.",
      cta_phone: "Телефон",
      cta_email: "Email",
      cta_address: "Адрес",
      cta_button: "Отправить сообщение",

      footer_cta_title: "Сделайте Первый Шаг к Здоровой Жизни",
      footer_cta_text: "Попробуйте наши продукты сегодня и почувствуйте разницу.",
      footer_cta_button: "Купить сейчас",
    },
  }

  const tr = translations[currentLang as keyof typeof translations] || translations.uz

  const stats = [
    { value: "15+", label: tr.stat_years, icon: Clock },
    { value: "100K+", label: tr.stat_customers, icon: Users },
    { value: "500+", label: tr.stat_products, icon: Leaf },
    { value: "14", label: tr.stat_regions, icon: Globe },
  ]

  const values = [
    { icon: Gem, title: tr.value_1_title, desc: tr.value_1_desc, color: "from-emerald-500 to-green-600" },
    { icon: Heart, title: tr.value_2_title, desc: tr.value_2_desc, color: "from-rose-500 to-pink-600" },
    { icon: Lightbulb, title: tr.value_3_title, desc: tr.value_3_desc, color: "from-amber-500 to-orange-600" },
    { icon: Shield, title: tr.value_4_title, desc: tr.value_4_desc, color: "from-blue-500 to-indigo-600" },
  ]

  const history = [
    { year: tr.history_1_year, title: tr.history_1_title, desc: tr.history_1_desc },
    { year: tr.history_2_year, title: tr.history_2_title, desc: tr.history_2_desc },
    { year: tr.history_3_year, title: tr.history_3_title, desc: tr.history_3_desc },
    { year: tr.history_4_year, title: tr.history_4_title, desc: tr.history_4_desc },
    { year: tr.history_5_year, title: tr.history_5_title, desc: tr.history_5_desc },
    { year: tr.history_6_year, title: tr.history_6_title, desc: tr.history_6_desc },
  ]

  const team = [
    { name: tr.team_1_name, role: tr.team_1_role, image: "/professional-businessman-portrait.png" },
    { name: tr.team_2_name, role: tr.team_2_role, image: "/professional-businesswoman-portrait.png" },
    { name: tr.team_3_name, role: tr.team_3_role, image: "/quality-control-specialist-portrait.jpg" },
    { name: tr.team_4_name, role: tr.team_4_role, image: "/customer-service-woman-portrait.png" },
  ]

  const certificates = [
    { name: tr.cert_1, icon: Award },
    { name: tr.cert_2, icon: Shield },
    { name: tr.cert_3, icon: CheckCircle },
    { name: tr.cert_4, icon: Leaf },
  ]

  const process = [
    { title: tr.process_1_title, desc: tr.process_1_desc, icon: Leaf },
    { title: tr.process_2_title, desc: tr.process_2_desc, icon: Shield },
    { title: tr.process_3_title, desc: tr.process_3_desc, icon: Sparkles },
    { title: tr.process_4_title, desc: tr.process_4_desc, icon: CheckCircle },
    { title: tr.process_5_title, desc: tr.process_5_desc, icon: Award },
    { title: tr.process_6_title, desc: tr.process_6_desc, icon: Truck },
  ]

  const testimonials = [
    { text: tr.testimonial_1_text, name: tr.testimonial_1_name, role: tr.testimonial_1_role },
    { text: tr.testimonial_2_text, name: tr.testimonial_2_name, role: tr.testimonial_2_role },
    { text: tr.testimonial_3_text, name: tr.testimonial_3_name, role: tr.testimonial_3_role },
  ]

  return (
    <main className="min-h-screen">
      {/* 1. Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/30">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-float-slow" />
        </div>

        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                {tr.hero_badge}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-balance">
                {tr.hero_title} <span className="gradient-text">{tr.hero_title_accent}</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl">{tr.hero_subtitle}</p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="rounded-xl shadow-lg hover:shadow-xl transition-all btn-glow">
                  <Phone className="w-5 h-5 mr-2" />
                  {tr.hero_cta}
                </Button>
                <Button size="lg" variant="outline" className="rounded-xl bg-transparent" asChild>
                  <Link to="/products">
                    {tr.hero_cta_secondary}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="scroll-animate-scale delay-200 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/natural-herbal-medicine-production-laboratory.jpg"
                  alt="TabiatShop production"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-card p-6 rounded-2xl shadow-xl border animate-float">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Award className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">15+</p>
                    <p className="text-sm text-muted-foreground">{tr.stat_years}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Stats Section */}
      <section className="py-16 bg-card border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className={`scroll-animate delay-${index * 100} text-center`}>
                <div className="w-16 h-16 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                  <stat.icon className="w-8 h-8 text-primary" />
                </div>
                <p className="text-4xl font-bold gradient-text mb-2">{stat.value}</p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Mission Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-animate-left">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Target className="w-4 h-4" />
                {tr.mission_badge}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{tr.mission_title}</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{tr.mission_text}</p>
            </div>
            <div className="scroll-animate-right delay-200">
              <div className="relative">
                <img
                  src="/happy-uzbek-family-healthy-lifestyle.jpg"
                  alt="Mission"
                  className="rounded-3xl shadow-xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Vision Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="scroll-animate-left order-2 lg:order-1">
              <img
                src="/futuristic-sustainable-natural-products-factory.jpg"
                alt="Vision"
                className="rounded-3xl shadow-xl w-full h-auto"
              />
            </div>
            <div className="scroll-animate-right order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Eye className="w-4 h-4" />
                {tr.vision_badge}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{tr.vision_title}</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{tr.vision_text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Values Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              {tr.values_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.values_title}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{tr.values_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card
                key={index}
                className={`scroll-animate delay-${index * 100} hover-lift border-0 shadow-lg overflow-hidden`}
              >
                <CardContent className="p-6">
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-4`}
                  >
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 6. History Timeline Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              {tr.history_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.history_title}</h2>
            <p className="text-muted-foreground">{tr.history_subtitle}</p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary/20 hidden md:block" />

            <div className="space-y-12">
              {history.map((item, index) => (
                <div
                  key={index}
                  className={`scroll-animate delay-${index * 100} relative flex items-center ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    <Card className="inline-block hover-lift">
                      <CardContent className="p-6">
                        <span className="text-3xl font-bold gradient-text">{item.year}</span>
                        <h3 className="text-xl font-semibold mt-2 mb-2">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-primary rounded-full border-4 border-background hidden md:block" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* 8. Certificates Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              {tr.certificates_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.certificates_title}</h2>
            <p className="text-muted-foreground">{tr.certificates_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certificates.map((cert, index) => (
              <Card
                key={index}
                className={`scroll-animate-scale delay-${index * 100} hover-lift hover-glow text-center`}
              >
                <CardContent className="p-8">
                  <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <cert.icon className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">{cert.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 9. Process Section */}
      <section className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {tr.process_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.process_title}</h2>
            <p className="text-muted-foreground">{tr.process_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {process.map((step, index) => (
              <Card key={index} className={`scroll-animate delay-${index * 100} hover-lift card-shine`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">{step.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Partners Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              {tr.partners_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.partners_title}</h2>
            <p className="text-muted-foreground">{tr.partners_subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
            {[1, 2, 3, 4, 5, 6].map((_, index) => (
              <div
                key={index}
                className={`scroll-animate delay-${index * 50} flex items-center justify-center p-6 bg-card rounded-2xl border hover-lift`}
              >
                <img
                  src={`/generic-company-logo.png`}
                  alt={`Partner ${index + 1}`}
                  className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 w-[120px] h-[60px] object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 scroll-animate">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              <Quote className="w-4 h-4" />
              {tr.testimonials_badge}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.testimonials_title}</h2>
            <p className="text-muted-foreground">{tr.testimonials_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <Card key={index} className={`scroll-animate delay-${index * 100} hover-lift`}>
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{item.text}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">{item.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-muted-foreground">{item.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 12. Contact CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.cta_title}</h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto">{tr.cta_subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="scroll-animate delay-100 text-center">
              <div className="w-16 h-16 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{tr.cta_phone}</h3>
              <p className="text-primary-foreground/80">+998 90 123 45 67</p>
            </div>
            <div className="scroll-animate delay-200 text-center">
              <div className="w-16 h-16 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-4">
                <Mail className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{tr.cta_email}</h3>
              <p className="text-primary-foreground/80">info@tabiatshop.uz</p>
            </div>
            <div className="scroll-animate delay-300 text-center">
              <div className="w-16 h-16 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8" />
              </div>
              <h3 className="font-semibold mb-2">{tr.cta_address}</h3>
              <p className="text-primary-foreground/80">Toshkent, O'zbekiston</p>
            </div>
          </div>

          <div className="text-center scroll-animate delay-400">
            <Button size="lg" variant="secondary" className="rounded-xl shadow-lg" asChild>
              <Link to="/contact">
                {tr.cta_button}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="scroll-animate bg-gradient-to-br from-card via-card to-primary/5 rounded-3xl p-12 text-center border shadow-xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{tr.footer_cta_title}</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">{tr.footer_cta_text}</p>
            <Button size="lg" className="rounded-xl shadow-lg btn-glow" asChild>
              <Link to="/products">
                {tr.footer_cta_button}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  )
}
